import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button, Text, Dropdown, Option } from '@fluentui/react-components';
import * as microsoftTeams from '@microsoft/teams-js';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import styles from './GameLauncher.module.scss';

const gameTypes = [
  // {
  //   name: 'Trivia Quiz',
  //   value: 'trivia-quizzes',
  //   route: '/task-module/trivia-quizzes',
  //   icon: '🎯'
  // },
  // {
  //   name: 'Image Quiz',
  //   value: 'image-quiz',
  //   route: '/task-module/image-quiz',
  //   icon: '🖼️'
  // },
  // {
  //   name: 'True or False',
  //   value: 'true-or-false',
  //   route: '/task-module/true-or-false',
  //   icon: '✅'
  // },
  {
    name: 'Water Cooler',
    value: 'watercooler',
    route: '/task-module/watercooler',
    icon: '💬'
  },
  // {
  //   name: 'Gotcha!',
  //   value: 'gotcha',
  //   route: '/task-module/gotcha',
  //   icon: '🎉'
  // },
  // {
  //   name: 'Pictionary',
  //   value: 'pictionary',
  //   route: '/task-module/pictionary',
  //   icon: '🎨'
  // },
  {
    name: 'Custom Poll',
    value: 'custom-poll',
    route: '/task-module/custom-poll',
    icon: '📊'
  },
  // {
  //   name: 'Unpopular Opinions',
  //   value: 'unpopular-opinion',
  //   route: '/task-module/unpopular-opinion',
  //   icon: '🤔'
  // }
];

const GameLauncher = ({ darkTheme, userDetails, channelData }) => {
  const [selectedChannel, setSelectedChannel] = useState('');
  const [selectedGame, setSelectedGame] = useState('');
  const [loading, setLoading] = useState(false);

  // Flatten channels from all teams into a single array
  const flattenedChannels = channelData?.allChannels?.flatMap(item => {
    // If item has channels array (regular team/channel structure)
    if (item.channels && Array.isArray(item.channels)) {
      return item.channels.map(channel => {
        const displayName = `${channel.name || channel.id} (${item.name || 'Unknown Team'})`;
        return {
          id: channel.id,
          name: channel.name,
          teamName: item.name,
          teamId: item.id,
          displayName: displayName,
          isGroupChat: false
        };
      });
    }
    // If item is a group chat (no channels property, has isGroupChat: true)
    else if (item.isGroupChat) {
      const displayName = `${item.name || item.id} (Group Chat)`;
      return [{
        id: item.id,
        name: item.name,
        teamName: 'Group Chat',
        teamId: item.id,
        displayName: displayName,
        isGroupChat: true
      }];
    }
    // Return empty array if neither structure matches
    return [];
  }) || [];

  const isChannelDataLoaded = channelData !== undefined;
  const hasChannels = flattenedChannels.length > 0;
  
  const handleChannelSelect = (_, data) => {
    setSelectedChannel(data.optionValue);
  };

  const handleGameSelect = (_, data) => {
    setSelectedGame(data.optionValue);
  };

  const handleLaunchGame = () => {
    if (!selectedChannel || !selectedGame) {
      toast.error('Please select both a channel and game type');
      return;
    }

    // Find the selected channel in our flattened array
    const selectedChannelData = flattenedChannels.find(ch => ch.id === selectedChannel);
    const selectedGameData = gameTypes.find(game => game.value === selectedGame);

    if (!selectedChannelData || !selectedGameData) {
      toast.error('Invalid selection. Please try again.');
      return;
    }

    setLoading(true);

    try {
      microsoftTeams.app.initialize();
      // Build URL with channel context using the actual channel ID
      const taskModuleUrl = `${window.location.origin}${selectedGameData.route}?channelId=${selectedChannelData.id}&channelName=${encodeURIComponent(selectedChannelData.name)}&teamId=${selectedChannelData.teamId}&teamName=${encodeURIComponent(selectedChannelData.teamName)}&startedVia=home`;
      
      microsoftTeams.tasks.startTask({
        url: taskModuleUrl,
        height: 600,
        width: 800,
        title: `Launch ${selectedGameData.name}`,
        completionBotId: process.env.REACT_APP_TEAMS_CLIENT_ID
      });

      toast.success(`Opening ${selectedGameData.name} for ${selectedChannelData.displayName}`);
    } catch (error) {
      console.error('Error launching game:', error);
      toast.error('Failed to launch game. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const canLaunch = selectedChannel && selectedGame && !loading;
  
  // Find the selected channel's display name for the dropdown
  const selectedChannelDisplayName = selectedChannel 
    ? flattenedChannels.find(ch => ch.id === selectedChannel)?.displayName 
    : '';

  return (
    <div className={classNames(styles.gameLauncher, { [styles.darkTheme]: darkTheme })}>
      <div className={styles.launcherContent}>
        <div className={styles.launcherInfo}>
          <div className={classNames(styles.launcherTitle, { [styles.darkTitle]: darkTheme })}>
            🎮 Launch Game
          </div>
          <Text className={classNames(styles.launcherDescription, { [styles.darkDescription]: darkTheme })}>
            Start a game in any channel to engage your team
          </Text>
          <Text className={classNames(styles.trialNotice, { [styles.darkTrialNotice]: darkTheme })}>
            🎯 Trial Mode: Limited game formats available here. Launch Trivia in channel to see all games!
          </Text>
        </div>

        {/* Show controls only when channels are available */}
        {isChannelDataLoaded && hasChannels && (
          <div className={styles.controls}>
            <div className={styles.dropdownGroup}>
              <Text className={classNames(styles.label, { [styles.darkLabel]: darkTheme })}>
                Select Channel
              </Text>
              <Dropdown
                placeholder="Choose a channel"
                value={selectedChannelDisplayName}
                onOptionSelect={handleChannelSelect}
                className={styles.dropdown}
              >
                {flattenedChannels.map((channel) => (
                  <Option key={channel.id} value={channel.id}>
                    #{channel.displayName}
                  </Option>
                ))}
              </Dropdown>
            </div>

            <div className={styles.dropdownGroup}>
              <Text className={classNames(styles.label, { [styles.darkLabel]: darkTheme })}>
                Select Game
              </Text>
              <Dropdown
                placeholder="Choose a game type"
                value={selectedGame}
                onOptionSelect={handleGameSelect}
                className={styles.dropdown}
              >
                {gameTypes.map((game) => (
                  <Option key={game.value} value={game.value}>
                    {game.icon} {game.name}
                  </Option>
                ))}
              </Dropdown>
            </div>

            <Button 
              appearance="primary" 
              size="medium"
              onClick={handleLaunchGame}
              disabled={!canLaunch}
              className={styles.launchButton}
            >
              {loading ? 'Launching...' : 'Launch Game'}
            </Button>
          </div>
        )}

        {/* Loading state */}
        {!isChannelDataLoaded && (
          <div className={classNames(styles.loadingState, { [styles.darkState]: darkTheme })}>
            <Text className={classNames(styles.loadingText, { [styles.darkText]: darkTheme })}>
              ⏳ Loading your channels...
            </Text>
          </div>
        )}

        {/* No channels state */}
        {isChannelDataLoaded && !hasChannels && (
          <div className={classNames(styles.noChannelsState, { [styles.darkState]: darkTheme })}>
            <Text className={classNames(styles.noChannelsTitle, { [styles.darkText]: darkTheme })}>
              🎮 No channels available for launching games. You need access to channels where Trivia is installed.
            </Text>
            <Text className={classNames(styles.noChannelsAction, { [styles.darkText]: darkTheme })}>
              Contact your team admin to install Trivia in your channels or request access.
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

GameLauncher.propTypes = {
  darkTheme: PropTypes.bool,
  userDetails: PropTypes.shape({
    isAdmin: PropTypes.bool,
    name: PropTypes.string
  }),
  channelData: PropTypes.shape({
    allChannels: PropTypes.array
  })
};

GameLauncher.defaultProps = {
  darkTheme: false,
  userDetails: { isAdmin: false, name: '' },
  channelData: { allChannels: [] }
};

const mapStateToProps = ({ home: { channelData } }) => ({
  channelData
});

export default connect(mapStateToProps)(GameLauncher);
